import { supabase } from '../lib/supabase';
import { ProgramClass } from '../types';
import { selectInChunks, selectAllPages } from '../utils/chunkedIn';

/**
 * 특정 프로그램의 모든 반 조회
 */
export const getProgramClasses = async (programId: string): Promise<ProgramClass[]> => {
  try {
    const { data, error } = await supabase
      .from('program_classes')
      .select('*')
      .eq('program_id', programId)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching program classes:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Get program classes error:', error);
    throw error;
  }
};

/**
 * 반 ID로 단일 반 조회
 */
export const getProgramClass = async (classId: string): Promise<ProgramClass | null> => {
  try {
    const { data, error } = await supabase
      .from('program_classes')
      .select('*')
      .eq('id', classId)
      .single();

    if (error) {
      console.error('Error fetching program class:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get program class error:', error);
    throw error;
  }
};

/**
 * 반 생성
 */
export interface CreateProgramClassData {
  program_id: string;
  class_name: string;

  // 정원/대기
  max_students: number;
  waiting_list: number;

  // 신청 기간
  registration_start_date: string;
  registration_start_time?: string;
  registration_end_date: string;
  registration_end_time?: string;

  // 교육 기간
  course_start_date: string;
  course_end_date: string;
  course_days?: string[];
  course_start_time?: string;
  course_end_time?: string;
  total_sessions?: number;

  // 수강료
  tuition: number;
  material_fee: number;
  payment_deadline?: string;
  refund_deadline?: string;

  // 강사/장소
  instructor?: string;
  institution?: string;
  location?: string;
  contact_person?: string;
  contact_phone?: string;

  // 메타 정보
  selection_method: 'first-come' | 'lottery';
  status?: 'upcoming' | 'recruiting' | 'ongoing' | 'completed';
  status_mode?: 'auto' | 'manual';
  is_published?: boolean;
  display_order?: number;
  remarks?: string;
  skip_overlap_check?: boolean;
}

export const createProgramClass = async (
  classData: CreateProgramClassData
): Promise<ProgramClass> => {
  try {
    const { data, error } = await supabase
      .from('program_classes')
      .insert({
        ...classData,
        status: classData.status || 'upcoming',
        status_mode: classData.status_mode || 'auto',
        is_published: classData.is_published ?? true,
        display_order: classData.display_order || 0,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating program class:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create program class error:', error);
    throw error;
  }
};

/**
 * 반 수정
 */
export interface UpdateProgramClassData extends Partial<CreateProgramClassData> {
  id: string;
}

export const updateProgramClass = async (
  classData: UpdateProgramClassData
): Promise<ProgramClass> => {
  try {
    const { id, ...updateData } = classData;

    const { data, error } = await supabase
      .from('program_classes')
      .update({
        ...updateData,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating program class:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Update program class error:', error);
    throw error;
  }
};

/**
 * 반 삭제
 */
export const deleteProgramClass = async (classId: string): Promise<void> => {
  try {
    // 먼저 해당 반에 신청자가 있는지 확인
    const { data: applications, error: checkError } = await supabase
      .from('applications')
      .select('id')
      .eq('class_id', classId)
      .in('status', ['registered', 'waiting'])
      .limit(1);

    if (checkError) {
      console.error('Error checking applications:', checkError);
      throw checkError;
    }

    if (applications && applications.length > 0) {
      throw new Error('신청자가 있는 반은 삭제할 수 없습니다. 먼저 신청자를 모두 취소해주세요.');
    }

    // 신청자가 없으면 삭제
    const { error } = await supabase
      .from('program_classes')
      .delete()
      .eq('id', classId);

    if (error) {
      console.error('Error deleting program class:', error);
      throw error;
    }
  } catch (error) {
    console.error('Delete program class error:', error);
    throw error;
  }
};

/**
 * 반의 신청 현황 조회
 */
export interface ClassApplicationStats {
  class_id: string;
  registered_count: number;
  waiting_count: number;
  cancelled_count: number;
  total_count: number;
}

export const getClassApplicationStats = async (
  classId: string
): Promise<ClassApplicationStats> => {
  try {
    const { data, error } = await supabase
      .from('applications')
      .select('status')
      .eq('class_id', classId);

    if (error) {
      console.error('Error fetching application stats:', error);
      throw error;
    }

    const stats = {
      class_id: classId,
      registered_count: 0,
      waiting_count: 0,
      cancelled_count: 0,
      total_count: data?.length || 0,
    };

    data?.forEach((app) => {
      if (app.status === 'registered') stats.registered_count++;
      else if (app.status === 'waiting') stats.waiting_count++;
      else if (app.status === 'cancelled') stats.cancelled_count++;
    });

    return stats;
  } catch (error) {
    console.error('Get class application stats error:', error);
    throw error;
  }
};

/**
 * 여러 반의 신청 현황 일괄 조회
 */
export const getBatchClassApplicationStats = async (
  classIds: string[]
): Promise<Record<string, ClassApplicationStats>> => {
  try {
    if (classIds.length === 0) return {};

    // 반 관리처럼 전체 반을 한 번에 넘기면 신청 건수가 1,000행 상한을 넘는다.
    // id 목록은 나눠 보내고(URL 길이), 각 조각은 range로 끝까지 읽는다(행 수).
    const { data, error } = await selectInChunks<{ class_id: string; status: string }>(
      classIds,
      slice => selectAllPages<{ class_id: string; status: string }>((from, to) => supabase
        .from('applications')
        .select('class_id, status')
        .in('class_id', slice)
        .order('id')
        .range(from, to))
    );

    if (error) {
      console.error('Error fetching batch application stats:', error);
      throw error;
    }

    // 초기화
    const statsMap: Record<string, ClassApplicationStats> = {};
    classIds.forEach((classId) => {
      statsMap[classId] = {
        class_id: classId,
        registered_count: 0,
        waiting_count: 0,
        cancelled_count: 0,
        total_count: 0,
      };
    });

    // 집계
    data?.forEach((app) => {
      const stats = statsMap[app.class_id];
      if (stats) {
        stats.total_count++;
        if (app.status === 'registered') stats.registered_count++;
        else if (app.status === 'waiting') stats.waiting_count++;
        else if (app.status === 'cancelled') stats.cancelled_count++;
      }
    });

    return statsMap;
  } catch (error) {
    console.error('Get batch class application stats error:', error);
    throw error;
  }
};

/**
 * 반 순서 일괄 업데이트
 */
export const updateClassesOrder = async (
  orderedClasses: { id: string; display_order: number }[]
): Promise<void> => {
  try {
    // 각 반의 display_order를 업데이트
    const promises = orderedClasses.map(({ id, display_order }) =>
      supabase
        .from('program_classes')
        .update({ display_order, updated_at: new Date().toISOString() })
        .eq('id', id)
    );

    const results = await Promise.all(promises);

    // 에러 체크
    const errors = results.filter((r) => r.error);
    if (errors.length > 0) {
      console.error('Error updating class order:', errors[0].error);
      throw errors[0].error;
    }
  } catch (error) {
    console.error('Update classes order error:', error);
    throw error;
  }
};
